import "./ProtocolSessions.scss";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { memo, useEffect, useState } from "react";
import {
    Accordion,
    Alert,
    Breadcrumb,
    Button,
    Card,
    Col,
    Container,
    Form,
    ListGroup,
    Modal,
    Row,
    Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { Protocols } from "../../api/Protocols";
import AppLoader from "../../components/AppLoader";
import { useToasts } from "../../contexts/toasts";
import { Protocol } from "../../models/Protocol";
import { Task } from "../../models/Task";
import IconButton from "../../components/IconButton";
import { TaskParameter, pType } from '../../models/TaskParameter';
import { Tasks } from "../../api/Tasks";

type Props = {
    task: Task;
    actions: React.ReactNode;
};

const TaskItem = memo(({ task, actions }: Props) => {
    return (
        <ListGroup.Item className="d-flex align-items-center">
            <div className="me-3">
                <FontAwesomeIcon icon={regular("check-circle")} size="lg" fixedWidth />
            </div>

            <Col>
                <h6 className="mb-0">{task.name}</h6>
            </Col>

            <Col md={3} className="text-end">
                {actions}
            </Col>
        </ListGroup.Item>
    );
});

export default function ProtocolDetail() {
    const { t } = useTranslation();
    const { id } = useParams();

    const [protocol, setProtocol] = useState<Protocol | null>();
    const [activeSession, setActiveSession] = useState<string | undefined>();
    const { showToast } = useToasts();

    const [showTaskModal, setShowTaskModal] = useState<boolean>(false);
    const [taskDetail, setTaskDetail] = useState<Task>()

    useEffect(() => {
        initProtocol();
        // eslint-disable-next-line
    }, []);

    /*     const initTasks = () => {
            Tasks.getTasks().then(
                ({ data: tasks }) => setTasks(tasks),
                (err) => {
                    const message = err.response?.data?.message || err.message;
                    const status = err.response?.status;
                    showToast!(
                        "tasks-error",
                        message,
                        `Tasks Error ${status ? `(${status})` : ""}`,
                        "danger"
                    );
                }
            );
        }; */

    const openTaskModal = (task: Task) => {
        if (!task.parameters.length) {

            Tasks.getTenantTask(String(task.id!)).then(({ data: tsk }) => {
                console.log(tsk)
                //setTaskDetail(tsk);
            }, (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                showToast!('task-error', message, `Task Detail Error ${status ? `(${status})` : ''}`, 'danger');
            })

        }
        setTaskDetail(task);

        setShowTaskModal(true);
    }

    const handleTaskModalClose = () => {
        setShowTaskModal(false);
        setTaskDetail(undefined);
    }


    const initProtocol = () => {
        Protocols.getProtocol(Number(id)!).then(
            ({ data: protocol }) => {
                setProtocol(protocol);
            },
            (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                showToast!(
                    "protocol-error",
                    message,
                    `Protocol Error ${status ? `(${status})` : ""}`,
                    "danger"
                );
            }
        );
    };

    const handleActiveSessionChange = (
        eventKey: string | string[] | null | undefined,
        evt: Object
    ) => {
        setActiveSession(
            Array.isArray(eventKey) ? eventKey[0] : (eventKey as string)
        );
    };

    const renderProtocolSessions = () => {
        if (protocol!.sessions.length === 0)
            return (
                <Alert className="py-2 mb-0">
                    <FontAwesomeIcon icon={solid("info-circle")} />
                    <span className="ms-2">No sessions available.</span>
                </Alert>
            );

        return (
            <Accordion
                className="sessions-accordions"
                activeKey={activeSession}
                onSelect={handleActiveSessionChange}
            >
                {protocol?.sessions.map((session, i) => (
                    <Accordion.Item key={i} eventKey={`${i}`}>
                        <Accordion.Header className="d-flex">
                            <Col>{session.label}</Col>
                        </Accordion.Header>

                        <Accordion.Body className="p-1">
                            {session.tasks.length === 0 && (
                                <Alert className="py-2 mb-0">
                                    <FontAwesomeIcon icon={solid("info-circle")} />
                                    <span className="ms-2">No tasks in this session.</span>
                                </Alert>
                            )}

                            {session.tasks!.length > 0 && (
                                <ListGroup>
                                    {session.tasks.map((task, j) => (
                                        <TaskItem
                                            key={task.id}
                                            task={task}
                                            actions={<>
                                                <IconButton onClick={() => openTaskModal(task)}>
                                                    <FontAwesomeIcon icon={solid('eye')} />
                                                </IconButton> </>}
                                        />
                                    ))}
                                </ListGroup>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>
        );
    };

    const renderCardContent = () => {
        return (
            <>
                <Card.Body className="">
                    <Row>
                        <Col>
                            <div className="d-flex align-items-center mb-2">
                                <Col>
                                    <h6 className="mb-0">Protocol Sessions</h6>
                                </Col>
                            </div>
                            {renderProtocolSessions()}
                        </Col>
                    </Row>
                </Card.Body>
            </>
        );
    };

    const renderAsyncContent = () => {
        return protocol ? (
            renderCardContent()
        ) : (
            <div className="d-flex py-3 justify-content-center">
                <AppLoader size={150} />
            </div>
        );
    };

    function customParamViewer(index: number) {

        switch (taskDetail?.parameters[index].type) {
            case pType.INTEGER:
                return (<td colSpan={4}>
                    <span> {taskDetail?.parameters[index].value?.toString() || taskDetail?.parameters[index].defaultValue?.toString() || taskDetail?.parameters[index].content?.toString()} </span>
                </td>
                )
            case pType.FLOAT:
                return (<td colSpan={4}>
                    <span>{taskDetail?.parameters[index].value?.toString() || taskDetail?.parameters[index].defaultValue?.toString() || taskDetail?.parameters[index].content?.toString()}</span>
                </td>
                )
            case pType.BOOLEAN:
                return (<>
                    <td colSpan={4}>
                        <span>{taskDetail?.parameters[index].value || taskDetail?.parameters[index].defaultValue || taskDetail?.parameters[index].content}</span>
                    </td>
                </>)
            case pType.TEXT:
                return <td colSpan={4}>
                    <span>{taskDetail?.parameters[index].value || taskDetail?.parameters[index].defaultValue || taskDetail?.parameters[index].content}</span>
                </td>
            case pType.SEEDFORRANDOM:
                return (<>
                    <td>
                        <span> {taskDetail?.parameters[index].value?.s0?.toString() || taskDetail?.parameters[index].content?.s0?.toString()} </span>
                    </td>
                    <td>
                        <span> {taskDetail?.parameters[index].value?.s1?.toString() || taskDetail?.parameters[index].content?.s1?.toString()} </span>
                    </td>
                    <td>
                        <span> {taskDetail?.parameters[index].value?.s2?.toString() || taskDetail?.parameters[index].content?.s2?.toString()} </span>
                    </td>
                    <td>
                        <span> {taskDetail?.parameters[index].value?.s3?.toString() || taskDetail?.parameters[index].content?.s3?.toString()} </span>
                    </td>
                </>)
            case pType.GENERATEDQUESTIONS:
                return (
                    <td colSpan={4}>
                        <Row className='d-flex justify-content-between mb-1 align-items-center'>
                            <Col md={4}>
                                <p className="mb-0 fw-bold"> Question</p>
                            </Col>
                            <Col md={4}>
                                <p className="mb-0 fw-bold"> Answers</p>
                            </Col>
                            <Col md={4}>
                                <p className="mb-0 fw-bold">  Correct </p>
                            </Col>
                        </Row >

                        {taskDetail?.parameters[index].content.map((x: any, gQi: number) => {
                            return (
                                <Row className="mb-1">
                                    < Col md={4} >
                                        <span> {taskDetail?.parameters[index].value ? taskDetail?.parameters[index].value[gQi].question : taskDetail?.parameters[index].content[gQi].question}</span>
                                    </Col>
                                    <Col md={4}>
                                        <span> {taskDetail?.parameters[index].value ? taskDetail?.parameters[index].value[gQi].answers || '' : ''} </span>
                                    </Col>
                                    <Col md={4}>
                                        <span> {taskDetail?.parameters[index].value ? taskDetail?.parameters[index].value[gQi].correct || taskDetail?.parameters[index].value[gQi].correct_from_platform : taskDetail?.parameters[index].content[gQi].correct_from_platform} </span>
                                    </Col >
                                </Row >
                            )
                        })
                        }

                    </td >
                )
            case pType.ENUM:
                return (<>
                    <td colSpan={4}>
                        <Row>
                            <Col md={8}>
                                <span>{taskDetail?.parameters[index].defaultValue?.toString() || taskDetail?.parameters[index].value?.toString() || taskDetail?.parameters[index].content?.toString()}</span>
                            </Col>
                            <Col md={4} className='border-left'>
                                <span className='float-end'>{taskDetail?.parameters[index].description}</span>
                            </Col>
                        </Row>
                    </td>
                </>)
            case pType.LISTINTEGER:
                return (<>
                    <td colSpan={4}>
                        <Row>
                            <Col >
                                <span>{taskDetail?.parameters[index].value} </span>
                            </Col>

                        </Row>
                    </td>
                </>)
            case pType.LISTFLOAT:
                return (<>
                    <td colSpan={4}>
                        <Row>
                            <Col >
                                <span> {taskDetail?.parameters[index].value} </span>
                            </Col>

                        </Row>
                    </td>
                </>)
            case pType.LISTTEXT:
                return (<>
                    <td colSpan={4}>
                        <Row>
                            <Col >
                                <span> {taskDetail?.parameters[index].value} </span>
                            </Col>

                        </Row>
                    </td>
                </>)
            default:
                return (<td></td>)
        }

    }

    return (
        <>
            <Container>
                <Breadcrumb>
                    <Breadcrumb.Item linkAs={Link} linkProps={{ to: window.__env.TENANT_PATH === 'core' ? `/protocols` : '/tenant-protocols' }}>
                        {t("pages.protocols")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>
                        {protocol?.name}
                    </Breadcrumb.Item>
                </Breadcrumb>

                <Card>{renderAsyncContent()}</Card>
            </Container>

            <Modal show={showTaskModal} onHide={handleTaskModalClose} dialogClassName="modal-xxl" size='xl'>
                <Modal.Header closeButton>
                    <Modal.Title> {taskDetail?.name} </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th scope='col' colSpan={1} className='w-25'> Parametro </th>

                                <th scope='col' colSpan={4}> Valore</th>
                                <th scope='col' colSpan={1}> Unità</th>
                            </tr>
                        </thead>
                        <tbody>
                            {taskDetail?.parameters.map((param, i) => (
                                <tr key={i}>
                                    <td colSpan={1}>{param.key}</td>

                                    {customParamViewer(i)}
                                    <td colSpan={1}>{param.unit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" className="border" size="sm" onClick={handleTaskModalClose}>{t('common.cancel')}</Button>
                </Modal.Footer>
            </Modal>

        </>
    );
}
